import React from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Users, Award, Briefcase, Globe, Shield, Brain, Zap, Target, CheckCircle2, Network } from 'lucide-react';

const TeamSection = () => {
  const rdStructure = [
    {
      title: 'Specialized Knowledge Access',
      icon: Brain,
      description: 'Top-tier expertise in AI compression, data formalization, and digital media from global talent pools',
      color: 'text-cyan-400'
    },
    {
      title: '3× Efficiency Advantage',
      icon: Zap,
      description: 'World-class innovation delivered at a fraction of traditional R&D lab costs',
      color: 'text-yellow-400'
    },
    {
      title: 'Dynamic Scalability',
      icon: Network,
      description: 'Scale with project demands while maintaining continuity of core leadership',
      color: 'text-purple-400'
    },
    {
      title: 'Core Technical Anchoring',
      icon: Target,
      description: 'Dedicated senior architects and engineers involved since inception',
      color: 'text-orange-400'
    }
  ];

  const ipOwnership = [
    {
      point: '100% Corporate Ownership',
      description: 'All work owned by the corporate entity with clean chain of title'
    },
    {
      point: 'Legal Framework',
      description: 'Strict IP assignment agreements, NDAs, and work-for-hire contracts enforceable across jurisdictions'
    },
    {
      point: 'Independent Audits',
      description: 'Verified clean chain of title on patents, source code, and all digital assets'
    },
    {
      point: 'Global Legal Protection',
      description: 'IP reinforced by Womble Bond Dickinson (US/UK) and Canadian counsel'
    }
  ];

  const expertise = [
    {
      area: 'Scientific Research',
      icon: Brain,
      description: 'Complex cognitive processing of vast data to reveal hidden patterns for optimal algorithms',
      color: 'text-blue-400'
    },
    {
      area: 'Information Processing',
      icon: Zap,
      description: 'Processing large amounts of information with minimal time to create greater value',
      color: 'text-cyan-400'
    },
    {
      area: 'Full Cycle Software Development',
      icon: Users,
      description: 'From architecture to deployment - smart design, efficient coding, effective testing',
      color: 'text-green-400'
    },
    {
      area: 'Digital Design & Art',
      icon: Briefcase,
      description: 'Outstanding graphics, GUI, and UX design magnified by digital technologies',
      color: 'text-purple-400'
    },
    {
      area: 'Blockchain & Digital Economy',
      icon: Globe,
      description: 'Immutability, decentralization, security through cryptography for data protection',
      color: 'text-orange-400'
    },
    {
      area: 'AI Powered Business Intelligence Solutions',
      icon: Target,
      description: 'Market analysis, revenue models, strategic planning and data-driven decision making',
      color: 'text-red-400'
    }
  ];

  const keyStats = [
    { label: 'Completed Projects', value: '16+', year: '2025' },
    { label: 'Science Synergies', value: '5+', year: '2025' },
    { label: 'Patent Applications', value: '4+', year: 'New' },
    { label: 'R&D Efficiency', value: '3×', year: 'vs Labs' }
  ];

  return (
    <section id="team" className="py-20 bg-slate-800 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 opacity-10">
        {Array.from({ length: 30 }).map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-cyan-400 rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              scale: [1, 1.5, 1],
              opacity: [0.3, 1, 0.3]
            }}
            transition={{
              duration: Math.random() * 3 + 2,
              repeat: Infinity,
              delay: Math.random() * 2
            }}
          />
        ))}
      </div>

      <div className="container mx-auto px-6 relative z-10">
        {/* Global R&D Structure */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <h3 className="text-3xl font-bold text-white text-center mb-4">
            Global R&D <span className="text-cyan-400">Structure</span>
          </h3>
          <p className="text-center text-gray-300 mb-8 max-w-3xl mx-auto">
            Globally optimized network accessing specialized knowledge and delivering innovation with unprecedented efficiency
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {rdStructure.map((item, index) => {
              const IconComponent = item.icon;
              return (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.2, duration: 0.5 }}
                  viewport={{ once: true }}
                  whileHover={{ scale: 1.05, y: -5 }}
                >
                  <Card className="bg-slate-900/50 border-slate-700 hover:border-cyan-400/40 transition-all duration-300 h-full">
                    <CardHeader className="text-center pb-3">
                      <motion.div
                        className={`w-12 h-12 mx-auto mb-3 rounded-full bg-slate-800 flex items-center justify-center ${item.color}`}
                        whileHover={{ rotate: 360 }}
                        transition={{ duration: 0.5 }}
                      >
                        <IconComponent size={24} />
                      </motion.div>
                      <CardTitle className="text-white text-base">{item.title}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <CardDescription className="text-gray-300 text-center text-sm leading-relaxed">
                        {item.description}
                      </CardDescription>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </motion.div>

        {/* IP Ownership & Legal Certainty */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <div className="bg-gradient-to-br from-green-900/20 to-emerald-900/20 rounded-2xl p-8 border border-green-400/30 backdrop-blur-sm">
            <div className="flex items-center justify-center mb-6">
              <Shield className="text-green-400 mr-3" size={32} />
              <h3 className="text-3xl font-bold text-white">IP Ownership & Legal Certainty</h3>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {ipOwnership.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1, duration: 0.5 }}
                  viewport={{ once: true }}
                  className="flex items-start space-x-3 bg-slate-900/30 p-4 rounded-lg border border-green-400/20"
                >
                  <CheckCircle2 className="text-green-400 flex-shrink-0 mt-1" size={20} />
                  <div>
                    <div className="text-green-300 font-semibold mb-1">{item.point}</div>
                    <p className="text-gray-300 text-sm">{item.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
            
            <div className="mt-6 text-center">
              <p className="text-gray-300 text-sm">
                Legal IP protection reinforced by <span className="text-green-400 font-semibold">Womble Bond Dickinson (US/UK)</span> and <span className="text-green-400 font-semibold">Canadian counsel</span>
              </p>
            </div>
          </div>
        </motion.div>

        {/* Internal Excellence Centers */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <div className="text-center mb-8">
            <h3 className="text-3xl font-bold text-white mb-4">
              Integrated <span className="text-purple-400">Excellence Centers</span>
            </h3>
            <p className="text-gray-300 max-w-3xl mx-auto">
              Comprehensive in-house capabilities spanning complex cross-science disciplinary projects and advanced technology development
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {expertise.map((area, index) => {
              const IconComponent = area.icon;
              return (
                <motion.div
                  key={area.area}
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.15, duration: 0.5 }}
                  viewport={{ once: true }}
                  whileHover={{ scale: 1.05 }}
                >
                  <Card className="bg-slate-900/50 border-slate-700 hover:border-purple-400/40 transition-all duration-300 h-full">
                    <CardHeader className="text-center pb-3">
                      <motion.div
                        className={`w-12 h-12 mx-auto mb-3 rounded-full bg-slate-800 flex items-center justify-center ${area.color}`}
                        whileHover={{ rotate: 360 }}
                        transition={{ duration: 0.5 }}
                      >
                        <IconComponent size={24} />
                      </motion.div>
                      <CardTitle className="text-white text-lg">{area.area}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <CardDescription className="text-gray-300 text-center leading-relaxed text-sm">
                        {area.description}
                      </CardDescription>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </motion.div>

        {/* Mission & Values */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <div className="bg-slate-900/50 rounded-2xl p-8 border border-cyan-400/20 backdrop-blur-sm">
            <h3 className="text-2xl font-bold text-white mb-6 text-center">Our Innovation Philosophy</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center space-y-3">
                <div className="text-cyan-400 font-semibold text-lg">Scientific Excellence</div>
                <p className="text-gray-300 text-sm">
                  Advancing modern media, communications and entertainment sectors through science-intensive breakthrough technologies
                </p>
              </div>
              <div className="text-center space-y-3">
                <div className="text-blue-400 font-semibold text-lg">Cross-Disciplinary Mastery</div>
                <p className="text-gray-300 text-sm">
                  Breakthrough innovation emerges when knowledge from one domain infuses into another, creating unprecedented solutions
                </p>
              </div>
              <div className="text-center space-y-3">
                <div className="text-purple-400 font-semibold text-lg">Global Impact</div>
                <p className="text-gray-300 text-sm">
                  International expertise with unwavering core leadership and dynamic adaptability to project demands
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default TeamSection;